import { sortBy } from "client_legacy/ndaw/arrayHelpers";
import { State } from "zustand";
import {
	PersistOptions,
	StateStorage,
	StorageValue
} from "./notPublic";
import { isNotNil } from "client/Misc/utils";
import { never } from "client_legacy/ndaw/errors";

export type IZustandMigrator<TFinalState> = {
	version: number;
	migrate: (persistedState: any, version: number | undefined) => TFinalState;
};

export type DownInstruction = "keep-upgraded-state" | "drop-data";

export type IZustandMigration = {
	migrateUp: (persistedState: any) => any;
	downInstruction: DownInstruction;
	version: number;
};

export type MigrationMetadata = {
	downInstruction: DownInstruction;
};

/**
 * Can be used to specify the initial version of the state, for example if state was migrated
 * before usage of migrator, and so the version is higher then the default 0
 */
export function defineInitialVersion(version: number): IZustandMigration {
	return {
		migrateUp: (state) => state,
		downInstruction: "drop-data",
		version
	};
}

export type WithMigratorStorageValue<S> = StorageValue<S>
	& {
		migrationMetadata: Record<number, MigrationMetadata>;
	};

export type WithMigratorPersistOptions<S extends State> = OverrideProperties<
	Omit<PersistOptions<S>, "migrate" | "version">,
	{
		getStorage: () => StateStorage;
		serialize?: (state: WithMigratorStorageValue<S>) => string | Promise<string>;
		deserialize?: (str: string) => WithMigratorStorageValue<S> | Promise<WithMigratorStorageValue<S>>;
	}
>;

export function withMigrator<S extends State>(
	options: WithMigratorPersistOptions<S>,
	...migrations: readonly IZustandMigration[]
): PersistOptions<S> {
	const allMigrations: IZustandMigration[] = sortBy(
		migrations,
		x => x.version
	);

	const targetVersion: number = allMigrations.length > 0
		? allMigrations[allMigrations.length - 1].version
		: 0;

	const baseSerialize: WithMigratorPersistOptions<S>["serialize"] = options.serialize
		?? ((state) => JSON.stringify(state));

	const baseDeserialize: WithMigratorPersistOptions<S>["deserialize"] = options.deserialize
		?? ((str): WithMigratorStorageValue<S> => JSON.parse(str));

	const migrate = async (
		persistedState: any,
		sourceVersion: number,
		targetVersion: number
	): Promise<S> => {
		if (targetVersion > sourceVersion) {
			const migrationsToApply: IZustandMigration[] = allMigrations.filter(
				m => m.version > sourceVersion
					&& m.version <= targetVersion
			);

			let previousVersion: number = sourceVersion;

			for (const migration of migrationsToApply) {
				const expectedVersion: number = previousVersion + 1;

				if (migration.version !== expectedVersion) {
					throw new Error(`There is a gap in migrations. No migration with version '${expectedVersion}' found.`);
				}

				persistedState = migration.migrateUp(persistedState);

				previousVersion = migration.version;
			}
		} else if (targetVersion < sourceVersion) {
			const storage: StateStorage = options.getStorage();

			const serializedState: string | null = await storage.getItem(options.name);

			if (typeof serializedState !== "string") {
				console.error("Unable to downgrade version. No migrations metadata present on the state.");
				throw new Error("Unable to downgrade version. No migrations metadata present on the state.");
			}

			const state: WithMigratorStorageValue<S> = await baseDeserialize(serializedState);

			// NOTE: Despite `migrationMetadata` being required on the `WithMigratorStorageValue` type
			// it still might be undefined in the storage (for example right after this code is introduced
			// there is no migration data in the storage)
			const migrationsMetadata: Record<number, MigrationMetadata> | undefined = state?.migrationMetadata;

			if (!migrationsMetadata) {
				console.error("Unable to downgrade version. No migrations metadata present on the state.");
				throw new Error("Unable to downgrade version. No migrations metadata present on the state.");
			}

			for (let i = sourceVersion; i > targetVersion; i--) {
				const migrationMetadata: MigrationMetadata | undefined = migrationsMetadata[i];

				let didDowngrade: boolean = false;

				if (isNotNil(migrationMetadata)) {
					const downInstruction: DownInstruction = migrationMetadata.downInstruction;

					switch (downInstruction) {
						case "keep-upgraded-state":
							didDowngrade = true;
							break;
						case "drop-data":
							// NOTE: We intentionally breaking the migration process, as a result the latest
							// version of data will stay in the storage, and store will have its initial state
							// based on the codebase. If user's actions wont mutate state, the latest state will
							// still be in the storage and after migration up it will be restored.
							throw new Error("Unable to downgrade");
						default:
							never(downInstruction);
					}
				}

				if (!didDowngrade) {
					console.error(`There is a gap in migrations. No downgrade instruction for version '${i}' found.`);
					throw new Error(`There is a gap in migrations. No downgrade instruction for version '${i}' found.`);
				}
			}
		}

		return persistedState;
	};

	const migrationMetadata: Record<number, MigrationMetadata> = generateMigrationsMetadata(allMigrations);

	const serialize = (storageValue: StorageValue<S>): string | Promise<string> => {
		const storageValueWithMigrationHistory: WithMigratorStorageValue<S> = {
			...storageValue,
			migrationMetadata
		};

		return baseSerialize(storageValueWithMigrationHistory);
	};

	const deserialize = async (str: string): Promise<StorageValue<S>> => {
		const {
			migrationMetadata: _,
			...storageValue
		} = await baseDeserialize(str);

		return storageValue;
	};

	return {
		...options,
		migrate: (state, version) => migrate(
			state,
			version ?? 0,
			targetVersion
		),
		version: targetVersion,
		serialize,
		// NOTE type definitions for zustand are incorrect, StorageValue<S>
		// is expected to be returned here, and not just plain S.
		deserialize: deserialize as unknown as PersistOptions<S>["deserialize"]
	};
}

function generateMigrationsMetadata(allMigrations: IZustandMigration[]): Record<number, MigrationMetadata> {
	const result: Record<number, MigrationMetadata> = {};

	for (const migration of allMigrations) {
		result[migration.version] = {
			downInstruction: migration.downInstruction
		};
	}

	return result;
}

